import { useEffect, useState } from 'react';
import { useParams, useSearch, useLocation } from 'wouter';
import { compradorTrpc, setSlugCompradorAtual } from '../../lib/compradorTrpc';
import { salvarSessaoComprador } from '../../lib/compradorAuth';
import { CompradorShell } from '../../components/comprador/CompradorShell';
import { rotaApp } from './util';

type Candidato = { id: string; nome: string };

/**
 * Tela que recebe o clique no link do e-mail (`/app/:slug/entrar?token=...`).
 * Normalmente entra direto (1 contrato). Só pede escolha quando o e-mail
 * casa com mais de um comprador NA MESMA org — nunca decide sozinho.
 */
export function EntrarComprador() {
  const { slug } = useParams<{ slug: string }>();
  const search = useSearch();
  const [, navigate] = useLocation();
  const token = new URLSearchParams(search).get('token');

  const [estado, setEstado] = useState<'verificando' | 'escolher' | 'erro'>('verificando');
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [entrando, setEntrando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Marca da incorporadora pelo slug — para que TODAS as telas (inclusive a de
  // link usado/expirado) fiquem dentro do mundo do comprador, com a marca dele,
  // nunca uma tela genérica nem qualquer coisa do B2B.
  const [org, setOrg] = useState<{ nome: string; logoUrl: string | null; corMarca: string | null } | null>(null);

  useEffect(() => {
    setSlugCompradorAtual(slug ?? null);
    if (!slug) return;
    compradorTrpc.auth.orgInfo.query({ orgSlug: slug }).then(setOrg).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!slug || !token) {
      setEstado('erro');
      setErro('Este link está incompleto.');
      return;
    }
    let vivo = true;
    compradorTrpc.auth.validarToken
      .query({ token })
      .then(async (r) => {
        if (!vivo) return;
        if (!r.precisaEscolher && r.unicoCompradorId) {
          await confirmar(r.unicoCompradorId);
          return;
        }
        setCandidatos(r.candidatos);
        setEstado('escolher');
      })
      .catch((e: Error) => {
        if (!vivo) return;
        setErro(e.message || 'Este link não é mais válido.');
        setEstado('erro');
      });
    return () => {
      vivo = false;
    };
  }, [slug, token]);

  async function confirmar(compradorId: string) {
    if (!slug || !token) return;
    setEntrando(true);
    try {
      const { sessionToken } = await compradorTrpc.auth.entrar.mutate({ token, compradorId });
      salvarSessaoComprador(slug, sessionToken);
      navigate(rotaApp(slug!), { replace: true });
    } catch (e) {
      setErro((e as Error).message || 'Não foi possível entrar. Peça um novo link.');
      setEstado('erro');
    } finally {
      setEntrando(false);
    }
  }

  if (estado === 'erro') {
    return (
      <CompradorShell logoUrl={org?.logoUrl} orgNome={org?.nome} corMarca={org?.corMarca}>
        <div className="text-center" data-testid="comprador-entrar-erro">
          <h1 className="text-[17px] font-semibold text-ink">Não foi possível entrar</h1>
          <p className="mt-2 text-sm text-ink-3">{erro}</p>
          <a href={rotaApp(slug ?? '')} className="mt-5 inline-block text-sm font-medium text-accent hover:underline">
            Pedir um novo link
          </a>
        </div>
      </CompradorShell>
    );
  }

  if (estado === 'escolher') {
    return (
      <CompradorShell logoUrl={org?.logoUrl} orgNome={org?.nome} corMarca={org?.corMarca}>
        <h1 className="text-[17px] font-semibold text-ink">Qual é o seu contrato?</h1>
        <p className="mt-1.5 text-sm text-ink-3">Encontramos mais de um contrato com este e-mail.</p>
        <div className="mt-4 space-y-2" data-testid="comprador-escolher-contrato">
          {candidatos.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={entrando}
              onClick={() => confirmar(c.id)}
              className="w-full rounded-xl border border-line-2 px-4 py-3 text-left text-sm font-medium text-ink transition-colors hover:border-accent disabled:opacity-60"
            >
              {c.nome}
            </button>
          ))}
        </div>
      </CompradorShell>
    );
  }

  return (
    <CompradorShell logoUrl={org?.logoUrl} orgNome={org?.nome} corMarca={org?.corMarca}>
      <p className="text-center text-sm text-ink-3" data-testid="comprador-verificando">
        Verificando seu acesso…
      </p>
    </CompradorShell>
  );
}
